import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  useMediaQuery,
  useTheme,
  Alert,
  SvgIcon,
  Typography,
} from '@mui/material';
import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import ProductsTab from './components/ProductsTab';
import { UserContext } from '../../components/shared/useUser';
import { Plus, Download01, ArrowUp } from '../../assets';
import { client, ReferenceInstance, ReferenceMarket, MarketItemUploadRequest, MarketItemUploadResult } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';
import { findSelectedStoreMarket } from './referenceLookup';

const ProductManagementPage = () => {
  const { t } = useTranslation('productManagement');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logError } = useLogError();

  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const [hasPermissionToCreateProduct, setHasPermissionToCreateProduct] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');

  const [showDownloadDialog, setShowDownloadDialog] = useState<boolean>(false);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [instances, setInstances] = useState<ReferenceInstance[]>([]);
  const [markets, setMarkets] = useState<ReferenceMarket[]>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<MarketItemUploadResult | null>(null);

  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageSeverity, setMessageSeverity] = useState<'success' | 'error' | 'info'>('success');

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    setHasPermissionToCreateProduct(hasPermissionTo(['ProductCreate', 'ConfigurationAdmin']));
    loadInstances();
  }, [hasPermissionTo]);

  useEffect(() => {
    setHasPermissionToCreateProduct(hasPermissionTo(['ProductCreate', 'ConfigurationAdmin']));

    // Initialize filter state from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const customerTypeIdParam = searchParams.get('customerTypeId');
    const searchTermParam = searchParams.get('searchTerm');

    if (companyIdParam) setCurrentFilterInstance(companyIdParam);
    if (customerTypeIdParam) setCurrentFilterMarket(customerTypeIdParam);
    if (searchTermParam) setCurrentFilterSearchTerm(searchTermParam);
  }, [hasPermissionTo, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId')) {
      loadStoreDefaultFilters();
    }
  }, [selectedStore, location.search]);

  const loadInstances = async () => {
    try {
      const response = await client.getAccessibleInstances();

      if (response.success && response.data) {
        setInstances(response.data);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  useEffect(() => {
    if (selectedInstance) {
      loadMarkets(selectedInstance);
    } else {
      setMarkets([]);
      setSelectedMarket('');
    }
  }, [selectedInstance]);

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getAccessibleMarkets(instanceId);

      if (response.success && response.data) {
        setMarkets(response.data);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadStoreDefaultFilters = async () => {
    if (!selectedStore?.franchiseUUID) return;

    try {
      const response = await client.getAccessibleMarkets();
      const storeMarket = response.success && response.data
        ? findSelectedStoreMarket(response.data, selectedStore.franchiseUUID)
        : undefined;

      if (!storeMarket) return;

      const instanceId = storeMarket.instanceId.toString();
      const marketId = storeMarket.id.toString();
      setCurrentFilterInstance(instanceId);
      setCurrentFilterMarket(marketId);
      navigate(`?${new URLSearchParams({ companyId: instanceId, customerTypeId: marketId }).toString()}`, { replace: true });
    } catch (error) {
      logError('Failed to load store default filters');
    }
  };

  const handleCreateNew = () => {
    const params = new URLSearchParams();
    if (currentFilterInstance) params.set('companyId', currentFilterInstance);
    if (currentFilterMarket) params.set('customerTypeId', currentFilterMarket);
    if (currentFilterSearchTerm) params.set('searchTerm', currentFilterSearchTerm);

    const queryString = params.toString();
    navigate(`/products/create${queryString ? '?' + queryString : ''}`);
  };

  const onCreateNew = () => {
    handleCreateNew();
  };

  const handleFilterChange = (instance: string, market: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleDownloadClick = () => {
    if (currentFilterInstance && currentFilterMarket) {
      setMessage(t('downloadStarting'));
      setMessageSeverity('info');
      setShowMessage(true);
      handleDownload(currentFilterInstance, currentFilterMarket);
    } else {
      setSelectedInstance('');
      setSelectedMarket('');
      setShowDownloadDialog(true);
    }
  };

  const handleUploadClick = () => {
    setSelectedInstance(currentFilterInstance || '');
    setSelectedMarket(currentFilterMarket || '');
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadDialog(true);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket('');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
  };

  const handleDownload = async (instanceId?: string, marketId?: string) => {
    const downloadInstance = instanceId || selectedInstance;
    const downloadMarket = marketId || selectedMarket;

    if (!downloadInstance || !downloadMarket) {
      setMessage('Please select both Instance and Market to download products');
      setMessageSeverity('error');
      setShowMessage(true);
      return;
    }

    setIsDownloading(true);

    if (!instanceId && !marketId) {
      setMessage(t('downloadStarting'));
      setMessageSeverity('info');
      setShowMessage(true);
    }

    try {
      const response = await client.downloadMarketItems(downloadInstance, downloadMarket);

      if (response.success && response.data) {
        const byteCharacters = atob(response.data.fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.fileName || 'products.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setMessage(t('downloadSuccessMessage'));
        setMessageSeverity('success');
        setShowMessage(true);
        setShowDownloadDialog(false);
      } else {
        setMessage(response.message || t('downloadErrorMessage'));
        setMessageSeverity('error');
        setShowMessage(true);
      }
    } catch (error) {
      logError('Failed to download products');
      setMessage(t('downloadErrorMessage'));
      setMessageSeverity('error');
      setShowMessage(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedInstance || !selectedMarket) {
      setMessage('Please select a file and ensure Instance and Market are selected');
      setMessageSeverity('error');
      setShowMessage(true);
      return;
    }

    setIsUploading(true);
    try {
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const uploadRequest: MarketItemUploadRequest = {
        companyId: parseInt(selectedInstance),
        customerTypeId: parseInt(selectedMarket),
        fileBase64: fileBase64,
      };

      const response = await client.uploadMarketItems(uploadRequest);

      if (response.success && response.data) {
        setUploadResult(response.data);
        setMessage(t('uploadSuccessMessage'));
        setMessageSeverity('success');
        setShowMessage(true);
      } else {
        setMessage(response.message || t('uploadErrorMessage'));
        setMessageSeverity('error');
        setShowMessage(true);
      }
    } catch (error) {
      logError('Failed to upload products');
      setMessage(t('uploadErrorMessage'));
      setMessageSeverity('error');
      setShowMessage(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const canViewProducts = hasPermissionTo(['ProductView', 'ConfigurationAdmin']);
  const canExportProducts = hasPermissionTo(['ProductExport', 'ConfigurationAdmin']);
  const canImportProducts = hasPermissionTo(['ProductImport', 'ConfigurationAdmin']);

  if (!canViewProducts) {
    return null;
  }

  return (
    <PageArea>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={4}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 4,
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('subtitle')}>{t('products')}</PageTitle>
        </Box>
        <Box
          width={isMobile ? '100%' : 'auto'}
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            gap: 2,
          }}
        >
          {canExportProducts && (
            <Button
              variant="secondary"
              size="lg"
              startIcon={<SvgIcon><Download01 /></SvgIcon>}
              onClick={handleDownloadClick}
              disabled={isDownloading}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {isDownloading ? t('downloading') : t('download')}
            </Button>
          )}
          {canImportProducts && (
            <Button
              variant="secondary"
              size="lg"
              startIcon={<SvgIcon><ArrowUp /></SvgIcon>}
              onClick={handleUploadClick}
              disabled={isDownloading || isUploading}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {isUploading ? t('uploading') : t('upload')}
            </Button>
          )}
          {hasPermissionToCreateProduct && (
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              disabled={isDownloading || isUploading}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createNew')}
            </Button>
          )}
        </Box>
      </Stack>

      <ProductsTab
        onCreateNew={onCreateNew}
        onFilterChange={handleFilterChange}
        initialInstance={currentFilterInstance}
        initialMarket={currentFilterMarket}
        initialSearchTerm={currentFilterSearchTerm}
      />

      {/* Download Dialog */}
      <Dialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('download')} {t('products')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByInstance')}
              </InputLabel>
              <Select
                value={selectedInstance}
                onChange={handleInstanceChange}
                label={t('filterByInstance')}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  {t('selectInstance')}
                </MenuItem>
                {instances.map((instance) => (
                  <MenuItem
                    key={instance.id}
                    value={instance.id.toString()}
                  >
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByMarket')}
              </InputLabel>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                label={t('filterByMarket')}
                displayEmpty
                notched
                disabled={!selectedInstance}
              >
                <MenuItem value="">
                  {t('selectMarket')}
                </MenuItem>
                {markets.map((market) => (
                  <MenuItem
                    key={market.id}
                    value={market.id.toString()}
                  >
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="lg"
            onClick={() => setShowDownloadDialog(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => handleDownload()}
            variant="primary"
            size="lg"
            disabled={!selectedInstance || !selectedMarket || isDownloading}
          >
            {isDownloading ? t('downloading') : t('download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('upload')} {t('products')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByInstance')}
              </InputLabel>
              <Select
                value={selectedInstance}
                onChange={handleInstanceChange}
                label={t('filterByInstance')}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  {t('selectInstance')}
                </MenuItem>
                {instances.map((instance) => (
                  <MenuItem
                    key={instance.id}
                    value={instance.id.toString()}
                  >
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByMarket')}
              </InputLabel>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                label={t('filterByMarket')}
                displayEmpty
                notched
                disabled={!selectedInstance}
              >
                <MenuItem value="">
                  {t('selectMarket')}
                </MenuItem>
                {markets.map((market) => (
                  <MenuItem
                    key={market.id}
                    value={market.id.toString()}
                  >
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography sx={{ mb: 2 }}>
                {t('uploadInstructions')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept=".xlsx,.xls,.xlsm"
                  style={{ display: 'none' }}
                  id="upload-file"
                  type="file"
                  onChange={handleFileChange}
                  aria-label={t('chooseFile')}
                />
                <label htmlFor="upload-file">
                  <Button
                    component="span"
                    size="lg"
                    sx={{ mr: 2 }}
                  >
                    {t('chooseFile')}
                  </Button>
                </label>
                <Typography component="span">
                  {uploadFile ? uploadFile.name : t('noFileSelected')}
                </Typography>
              </Box>
            </Box>
            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Typography>{t('uploadResults')}</Typography>
                <Typography>{t('created')}: {uploadResult.created}</Typography>
                <Typography>{t('updated')}: {uploadResult.updated}</Typography>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography color="error">{t('errors')}:</Typography>
                    {uploadResult.errors.map((error, index) => (
                      <Typography
                        key={index}
                        color="error"
                      >
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="lg"
            onClick={() => setShowUploadDialog(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleUpload}
            variant="primary"
            size="lg"
            disabled={!uploadFile || !selectedInstance || !selectedMarket || isUploading}
          >
            {isUploading ? t('uploading') : t('upload')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Message */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={messageSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea >
  );
};

export default ProductManagementPage;
